import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIdentity } from '@/entities/session';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';

import { useLogout } from '../model/logout';

/* Sign out, last in the top bar, behind a confirmation.

 * **Absent rather than disabled when nobody is signed in.** The identity read is what says a session
 * exists; while it is pending or has failed there is nothing to sign out *of*, and a live-looking
 * control that ends a session which may not exist is the same claim `UserChip` refuses to make with
 * a placeholder disc. Keyed on the same read as the chip, so the two cannot disagree about whether
 * there is a reader — a header showing a name with no way out, or a way out with no name, is a
 * header assembled from two answers.
 *
 * **It asks first, and that reverses my own earlier reasoning here.** The note this replaces argued
 * against a dialog: `ConfirmDialog` is for actions that are hard to reverse, signing out destroys
 * nothing, and a prompt teaches a reader to dismiss prompts without reading them — which makes the
 * ones that matter weaker. The project owner asked for the confirmation, which settles it, and the
 * argument was thinner than it read: signing out is *cheap to undo* and not cheap to suffer. On a
 * shared workstation the cost of a stray click is re-entering a password; on a laptop mid-task it is
 * losing an unsaved form somewhere behind the shell, and this control sits two pixels from the theme
 * toggle in a bar somebody clicks at speed. A confirmation on the one header control that discards
 * state is not the prompt that trains dismissal — the prompt that does that is the one on an action
 * with no consequence.
 *
 * **The message names what actually happens**, because that is the whole value of asking: the
 * session ends *in this tab*, and a token already issued is not revoked server-side — there is no
 * logout endpoint, deliberately, and `useLogout` carries the reasoning. Telling a reader "you will
 * be signed out" and leaving that unsaid would be the vaguer half of a true sentence on the one
 * screen where somebody may be signing out *because* they are worried.
 */
export function LogoutButton(): ReactElement | null {
  const { t } = useTranslation();
  const identity = useIdentity();
  const logOut = useLogout();
  const [asking, setAsking] = useState(false);

  if (identity.data === undefined) return null;

  return (
    <>
      <Button
        tone="secondary"
        size="compact"
        onClick={() => {
          setAsking(true);
        }}
        title={t('shell.logout')}
      >
        {/* Decorative: the button's own words are its accessible name, and a screen reader
            announcing the glyph before them would read the door as a second label. */}
        <svg
          className="lq-button__glyph"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          {/* A door with an arrow leaving it — the reference's mark. Drawn open on the right so the
              arrow reads as going out rather than coming in, which is the one way this glyph is
              commonly wrong. */}
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="m16 17 5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
        {t('shell.logout')}
      </Button>

      <ConfirmDialog
        open={asking}
        title={t('shell.logout.confirmTitle')}
        message={t('shell.logout.confirmAsk', { name: identity.data.display_name })}
        /* **"Sign out" and "Stay signed in", both the reference's own words**, and both are better
           than what they replace. `common.cancel` renders "Cancel", which on this question is
           ambiguous in the one way a confirmation cannot afford — cancel the signing out, or cancel
           whatever the reader was doing? Naming the outcome instead means neither answer has to be
           inferred from its position. The trigger in the bar still reads "Log out", which is also
           the reference's, so the two are deliberately not the same string. */
        confirmLabel={t('shell.logout.confirmLabel')}
        cancelLabel={t('shell.logout.stay')}
        /* `primary`, and this replaces a `secondary` whose argument was sound and answered a
           different question. That note read: the danger tone is for a write that cannot be taken
           back, and colouring this one the same would say signing out is that kind of act. It still
           would — `danger` is still wrong here. But the choice was never between `danger` and a
           ghost: the reference draws the confirming answer of this dialog as a *filled accent*
           button at weight 600, which reads as "this is the answer you came for" rather than as
           "this is dangerous". Two outlined buttons side by side say the opposite of what the
           dialog is for, which is that one of the two answers is the one being offered.

           It also moves where focus lands, and in the safe direction. `ConfirmDialog` focuses
           Cancel only for `danger`; with `primary` the platform's own `showModal` focuses the first
           focusable element, which is still Cancel because `closable={false}` puts nothing ahead of
           it. So a stray Enter on the keypress that opened this dialog still means "stay". */
        tone="primary"
        /* The surface's own restyle — argued in `system.css`. A modifier rather than a change to
           `.lq-confirm`, because that class is shared with four confirmations on screens this
           change did not review. */
        className="lq-confirm--signout"
        onConfirm={() => {
          /* The dialog is closed *before* the navigation rather than left for the unmount, so a
             logout that somehow failed to navigate does not leave a modal open over a signed-out
             shell. `useLogout` clears the credential first, so by this point there is no session
             behind whatever remains on screen. */
          setAsking(false);
          logOut();
        }}
        onCancel={() => {
          setAsking(false);
        }}
      />
    </>
  );
}
